import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { logger } from '../utils/logger';
import { globalToast } from '../utils/globalToast';
import { useAuth } from './AuthContext';
import { useCustomers } from './CustomerContext';
import { db } from '../config/firebase';
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  deleteField,
  limit,
  orderBy,
} from 'firebase/firestore';
import { COLLECTIONS } from '../config/constants';
import type { Order, OrderItem } from '../shared/types';

// Sipariş tipleri shared/types'tan re-export edilir (geri uyumluluk için).
export type { Order, OrderItem };

// Context tipi
interface OrderContextType {
  orders: Order[];
  addOrder: (order: Omit<Order, 'id' | 'createdAt'>) => void;
  updateOrder: (order: Order) => void;
  deleteOrder: (orderId: string) => void;
  clearAllOrders: () => void;
  clearCompletedOrders: () => void;
  
  // Dashboard için hesaplanan değerler
  getTodaysOrderCount: () => number;
  getPendingOrderCount: () => number;
  getCompletedOrderCount: () => number;
  getTodaysRevenue: () => number;
  
  // Filtreler
  getOrdersByDate: (date: string) => Order[];
  getOrdersByStatus: (status: Order['deliveryStatus']) => Order[];
  getPendingOrders: () => Order[];
  getCompletedOrders: () => Order[];
}

// Context oluşturma
const OrderContext = createContext<OrderContextType | undefined>(undefined);

// Hook
export const useOrders = () => {
  const context = useContext(OrderContext);
  if (context === undefined) {
    throw new Error('useOrders must be used within an OrderProvider');
  }
  return context;
};

// Provider props tipi
interface OrderProviderProps {
  children: ReactNode;
}

// Provider bileşeni
export const OrderProvider: React.FC<OrderProviderProps> = ({ children }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const { currentUser, loading } = useAuth();
  const isAdmin = (currentUser?.role || '').toLowerCase() === 'admin';

  // B.x-3c: CustomerContext fonksiyonları - window event yerine doğrudan kullanım
  const { updateCustomer, getCustomerById, getCustomerByFullAddress } = useCustomers();

  // Firestore realtime listener
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Auth kontrolü
    if (loading) return;
    if (!currentUser) {
      setOrders([]);
      return;
    }

    const colRef = collection(db, COLLECTIONS.ORDERS);
    
    // PERFORMANS OPTİMİZASYONU (Faz C-Acil):
    // Initial RAM ve TTI'yı azaltmak için limitler düşürüldü.
    // Cursor pagination Faz C-Type'ta eklenecek (sayfa altı "Daha fazla yükle").
    let colQuery;

    if (isAdmin) {
      // Admin: Son 100 sipariş (createdAt desc) — günlük operasyon için yeterli
      colQuery = query(colRef, orderBy('createdAt', 'desc'), limit(100));
    } else {
      // Müşteri: Kendi son 50 siparişi
      colQuery = query(colRef, where('userId', '==', currentUser.uid), limit(50));
    }

    const unsub = onSnapshot(
      colQuery,
      (snap) => {
        const list: Order[] = snap.docs.map((d) => ({ id: d.id, ...d.data() } as Order));
        
        // createdAt normalize et (Firestore Timestamp → string)
        const normalized = list.map((o: any) => ({
          ...o,
          createdAt: typeof o.createdAt === 'string'
            ? o.createdAt
            : new Date((o.createdAt?.seconds * 1000) || Date.now()).toISOString(),
        })) as Order[];
        
        setOrders(normalized.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')));
        // ordersChanged window event kaldırıldı - hiçbir yerde dinlenmiyordu (B.x-3a)
      },
      (error) => {
        logger.error('Firestore orders dinleme hatası:', error);
        setOrders([]);
      }
    );

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, isAdmin, loading]);

  // B.x-3c: Internal helper - müşterinin son sipariş tarihini güncelle
  const updateCustomerLastOrderDate = useCallback((customerId: string, date: string) => {
    if (!customerId || !date) return;
    const customer = getCustomerById(customerId);
    if (!customer) return;
    if (!customer.lastOrderDate || new Date(date).getTime() > new Date(customer.lastOrderDate).getTime()) {
      updateCustomer({ ...customer, lastOrderDate: date });
    }
  }, [getCustomerById, updateCustomer]);

  // B.x-3c: Internal helper - müşteri notlarını ekle
  const appendNotesToCustomer = useCallback((customerId: string | undefined, customerAddress: string | undefined, notes: string[]) => {
    if (!Array.isArray(notes) || notes.length === 0) return;

    const isSystemNote = (note: string): boolean => {
      const t = String(note || '').trim().toLowerCase();
      if (!t) return true;
      return (
        t.startsWith('teslimat saati:') ||
        t.startsWith('ödeme:') ||
        t.startsWith('odeme:') ||
        t.startsWith('hızlı sipariş:') ||
        t.startsWith('hizli siparis:')
      );
    };

    const resolvedCustomer = (customerId ? getCustomerById(customerId) : undefined)
      || (customerAddress ? getCustomerByFullAddress(String(customerAddress)) : undefined);
    if (!resolvedCustomer) return;

    const existing = (resolvedCustomer.notes || '').split('|').map((s) => s.trim()).filter(Boolean);
    const merged = [...existing];
    let changed = false;
    for (const n of notes) {
      const t = String(n || '').trim();
      if (!t) continue;
      if (isSystemNote(t)) continue;
      const lowerT = t.toLowerCase();
      const alreadyExists = merged.some(ex => ex.toLowerCase() === lowerT);
      if (!alreadyExists) {
        merged.push(t);
        changed = true;
      }
    }
    if (!changed) return;
    const nextNotes = merged.join(' | ');
    updateCustomer({ ...resolvedCustomer, notes: nextNotes });
  }, [getCustomerById, getCustomerByFullAddress, updateCustomer]);

  // Sipariş ekleme
  const addOrder = useCallback(async (orderData: Omit<Order, 'id' | 'createdAt'>) => {
    if (!currentUser) {
      globalToast.warning('Sipariş kaydedebilmek için lütfen giriş yapın.');
      return null;
    }
    const today = new Date();
    const defaultDate = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
    const effectiveCustomerId = orderData.customerId || currentUser?.uid || '';
    const effectiveCustomerName = orderData.customerName || currentUser?.displayName || '';
    
    try {
      const createdAt = new Date().toISOString();
      const cleanOrderData = {
        userId: currentUser.uid,
        userUid: currentUser.uid,
        customerId: effectiveCustomerId,
        customerName: effectiveCustomerName,
        customerPhone: orderData.customerPhone || '',
        customerAddress: orderData.customerAddress || '',
        customerNotes: Array.isArray(orderData.customerNotes) ? orderData.customerNotes : [],
        orderDetails: orderData.orderDetails || '',
        items: Array.isArray(orderData.items) ? orderData.items : [], // Sipariş tekrarlama için
        amount: orderData.amount || 0,
        // Müşteriler için her zaman 'unpaid' zorla, adminler seçebilir
        paymentStatus: (currentUser?.role === 'admin' ? orderData.paymentStatus : 'unpaid') || 'unpaid',
        paymentMethod: orderData.paymentMethod || 'cash_on_delivery',
        deliveryStatus: orderData.deliveryStatus || 'preparing',
        distributor: orderData.distributor || '',
        deliveryDate: orderData.deliveryDate || defaultDate,
        deliverySlot: orderData.deliverySlot || '',
        createdAt
      };
      
      const ref = await addDoc(collection(db, COLLECTIONS.ORDERS), cleanOrderData);

      // B.x-3c: Müşterinin son sipariş tarihini güncelle (window event yerine doğrudan çağrı)
      if (effectiveCustomerId) {
        updateCustomerLastOrderDate(effectiveCustomerId, cleanOrderData.deliveryDate);
      }

      // B.x-3c: Sipariş esnasında girilen müşteri notlarını müşteri kaydına aktar
      const notesToAppend: string[] = [];
      if (Array.isArray(orderData.customerNotes)) {
        notesToAppend.push(...orderData.customerNotes.map(n => String(n || '').trim()).filter(Boolean));
      }
      if ((orderData as any).notes && String((orderData as any).notes).trim()) {
        notesToAppend.push(String((orderData as any).notes).trim());
      }
      if (notesToAppend.length > 0) {
        appendNotesToCustomer(effectiveCustomerId, cleanOrderData.customerAddress, notesToAppend);
      }
      return ref.id;
    } catch (e) {
      logger.error('Sipariş ekleme hatası:', e);
      globalToast.error('Sipariş kaydedilirken hata oluştu: ' + (e as Error).message);
      return null;
    }
  }, [currentUser, updateCustomerLastOrderDate, appendNotesToCustomer]);

  // Sipariş güncelleme payload builder
  const buildOrderUpdatePayload = useCallback((order: Order) => {
    const { id, userUid, userId, ...rest } = order;
    const payload: Record<string, any> = {};

    Object.entries(rest).forEach(([key, value]) => {
      if (value === undefined) {
        payload[key] = deleteField();
      } else {
        payload[key] = value;
      }
    });

    payload.userUid = userUid || userId || null;
    return payload;
  }, []);

  const updateOrder = useCallback(async (updatedOrder: Order) => {
    try {
      const payload = buildOrderUpdatePayload(updatedOrder);
      await updateDoc(doc(db, COLLECTIONS.ORDERS, updatedOrder.id), payload);

      // B.x-3c: Siparişten müşteri notlarını kalıcı müşteri kaydına aktar (window event yerine doğrudan çağrı)
      const notesToAppend: string[] = [];
      if (Array.isArray(updatedOrder.customerNotes)) {
        notesToAppend.push(...updatedOrder.customerNotes.map(n => String(n || '').trim()).filter(Boolean));
      }
      if (updatedOrder.notes && String(updatedOrder.notes).trim()) {
        notesToAppend.push(String(updatedOrder.notes).trim());
      }
      if (notesToAppend.length > 0) {
        appendNotesToCustomer(updatedOrder.customerId, updatedOrder.customerAddress, notesToAppend);
      }
    } catch (e: any) {
      logger.error('Sipariş güncelleme hatası:', e);
      globalToast.error('Sipariş güncellenirken hata oluştu: ' + (e?.message || 'Bilinmeyen hata'));
    }
  }, [buildOrderUpdatePayload, appendNotesToCustomer]);

  // Sipariş silme
  const deleteOrder = useCallback(async (orderId: string) => {
    try {
      await deleteDoc(doc(db, COLLECTIONS.ORDERS, orderId));
    } catch (e: any) {
      logger.error('Sipariş silme hatası:', e);
      globalToast.error('Sipariş silinirken hata oluştu: ' + (e?.message || 'Bilinmeyen hata'));
    }
  }, []);

  // Tüm siparişleri temizle
  const clearAllOrders = useCallback(async () => {
    const ids = orders.map(o => o.id);
    try {
      await Promise.all(ids.map(id => deleteDoc(doc(db, COLLECTIONS.ORDERS, id)).catch(() => {})));
    } catch (e) {
      logger.error('Tüm siparişleri temizleme hatası:', e);
      globalToast.error('Tüm siparişleri temizlerken hata oluştu.');
    }
  }, [orders]);

  // Tamamlanan siparişleri temizle
  const clearCompletedOrders = useCallback(async () => {
    const completed = orders.filter(o => 
      o.deliveryStatus === 'delivered' || o.deliveryStatus === 'cancelled'
    );
    try {
      await Promise.all(completed.map(o => deleteDoc(doc(db, COLLECTIONS.ORDERS, o.id)).catch(() => {})));
    } catch (e) {
      logger.error('Tamamlananları temizleme hatası:', e);
      globalToast.error('Tamamlananları temizlerken hata oluştu.');
    }
  }, [orders]);

  // Memoized today string - recalculate only when date changes (every minute check)
  const todayString = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  }, []);

  // Memoized today's orders - single filter for all dashboard calculations
  const todaysOrders = useMemo(() => {
    return orders.filter(order => order.deliveryDate === todayString);
  }, [orders, todayString]);

  // Dashboard için memoized değerler - her render'da tekrar hesaplanmaz
  const todaysOrderCount = useMemo(() => todaysOrders.length, [todaysOrders]);

  const pendingOrderCount = useMemo(() => {
    return todaysOrders.filter(order =>
      order.deliveryStatus === 'preparing' || order.deliveryStatus === 'onway'
    ).length;
  }, [todaysOrders]);

  const completedOrderCount = useMemo(() => {
    return todaysOrders.filter(order =>
      order.deliveryStatus === 'delivered' || order.deliveryStatus === 'cancelled'
    ).length;
  }, [todaysOrders]);

  const todaysRevenue = useMemo(() => {
    return todaysOrders
      .filter(order =>
        order.deliveryStatus === 'delivered' &&
        order.paymentStatus === 'paid'
      )
      .reduce((total, order) => total + order.amount, 0);
  }, [todaysOrders]);

  // Getter functions now return memoized values
  const getTodaysOrderCount = useCallback(() => todaysOrderCount, [todaysOrderCount]);
  const getPendingOrderCount = useCallback(() => pendingOrderCount, [pendingOrderCount]);
  const getCompletedOrderCount = useCallback(() => completedOrderCount, [completedOrderCount]);
  const getTodaysRevenue = useCallback(() => todaysRevenue, [todaysRevenue]);

  // Filtre fonksiyonları
  const getOrdersByDate = useCallback((date: string) => {
    return orders.filter(order => order.deliveryDate === date);
  }, [orders]);

  const getOrdersByStatus = useCallback((status: Order['deliveryStatus']) => {
    return orders.filter(order => order.deliveryStatus === status);
  }, [orders]);

  const getPendingOrders = useCallback(() => {
    return orders.filter(order => 
      order.deliveryStatus === 'preparing' || order.deliveryStatus === 'onway'
    );
  }, [orders]);

  const getCompletedOrders = useCallback(() => {
    return orders.filter(order => 
      order.deliveryStatus === 'delivered' || order.deliveryStatus === 'cancelled'
    );
  }, [orders]);

  const contextValue = useMemo<OrderContextType>(() => ({
    orders,
    addOrder,
    updateOrder,
    deleteOrder,
    clearAllOrders,
    clearCompletedOrders,
    getTodaysOrderCount,
    getPendingOrderCount,
    getCompletedOrderCount,
    getTodaysRevenue,
    getOrdersByDate,
    getOrdersByStatus,
    getPendingOrders,
    getCompletedOrders,
  }), [orders, addOrder, updateOrder, deleteOrder, clearAllOrders, clearCompletedOrders, getTodaysOrderCount, getPendingOrderCount, getCompletedOrderCount, getTodaysRevenue, getOrdersByDate, getOrdersByStatus, getPendingOrders, getCompletedOrders]);

  return (
    <OrderContext.Provider value={contextValue}>
      {/* B.x-3c: CustomerLastOrderBridge kaldırıldı - fonksiyonlar doğrudan çağrılıyor */}
      {children}
    </OrderContext.Provider>
  );
};

export default OrderContext;

// B.x-3c: CustomerLastOrderBridge component kaldırıldı
// updateCustomerLastOrderDate ve appendNotesToCustomer fonksiyonları artık
// OrderProvider içinde tanımlanıyor ve doğrudan addOrder/updateOrder'dan çağrılıyor.
// Window event'ler kaldırıldı: updateCustomerLastOrder, appendCustomerNotes
